import React, { useContext, useEffect } from "react";
import {
  CategoryTitle,
  CopyToClipboardButton,
  Loader,
  LoadingIndicator
} from "elements";
import { NavLink, useHistory, useParams } from "polyrouter";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { Card, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import { AccessToken, useGetAccessToken } from "../useAccessTokens";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

const AccessTokenInformation: React.FC<{
  accessToken: AccessToken;
}> = ({ accessToken }) => {
  const { t } = useTranslation();

  const fields = [
    {
      key: "token",
      label: t("ACCESS_TOKEN_NAME"),
      content: <p>{accessToken.token}</p>
    },
    {
      key: "creationTime",
      label: t("DATE_CREATED"),
      content: <p>{toLicenseDateLocaleDateString(accessToken.creationTime)}</p>
    },
    {
      key: "lastUsedTime",
      label: t("LAST_USED"),
      content: <p>{toLicenseDateLocaleDateString(accessToken.lastUsedTime)}</p>
    }
  ];

  return (
    <>
      <Section>
        <Section.Header>{t("ACCESS_TOKEN_INFORMATION")}</Section.Header>
        <Card className="table-card">
          {makeColumnContentForCard(fields, 3)}
        </Card>
      </Section>
      <div>
        <CopyToClipboardButton
          name="copyToken"
          notifyName={t("ACCESS_TOKEN_NAME")}
          getTextToCopy={() => accessToken.token}
          iconButton={false}
        >
          {t("COPY")}
        </CopyToClipboardButton>
      </div>
    </>
  );
};

export const AccessTokenDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const history = useHistory();
  const { Content, Sidebar } = Layout;

  const { data, fetching } = useGetAccessToken({
    variables: { tenantId: tenantId, recordId: id }
  });

  useEffect(() => {
    if (!fetching && tenantId) {
      if (!data || tenantId !== data.tenantId) {
        history.push("/manage/licensing/clariti/access-tokens");
      }
    }
  }, [fetching, data, history, tenantId, id]);

  if (fetching) {
    return <LoadingIndicator />;
  }

  const token: AccessToken | undefined = data;

  return (
    <>
      <Sidebar>
        <span className="title-capitalized">{t("CLARITI")}</span>
        <ul className="menu router">
          <li>
            <NavLink nest exact to={""}>
              {t("OVERVIEW")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Content className="access-token-details">
        <Loader loading={fetching}>
          {token && (
            <>
              <Content.Title>
                <CategoryTitle
                  title={t("OVERVIEW")}
                  subtitle={t("ACCESS_TOKEN_DETAILS")}
                />
              </Content.Title>
              <AccessTokenInformation accessToken={token} />
            </>
          )}
        </Loader>
      </Content>
    </>
  );
};
